/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable no-console */
import { makeStyles } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import currencyFormatter from '@sprinx/formatting/currencyFormatter';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { currencyState } from '../../../api/appState';
import { customerPriceGroupState } from '../../../api/customer/customer';
import { CatalogueProduct, productVariantsQuery } from '../../../api/products/catalogue';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import CataloguePageListContentItemRowAddToCart from './CataloguePageListContentItemRowAddToCart';

type CataloguePageListContentItemWarningClassKey = 'root';
interface CataloguePageListContentItemWarningProps
  extends StandardDivProps<CataloguePageListContentItemWarningClassKey> {
  classes?: Record<'root', string>;
  className?: string;
  handleShowVariants?: any;
  product: CatalogueProduct;
  showVariants?: boolean;
  type: 'detail' | 'grid';
}

const useStyles = makeStyles((theme) => ({
  root: {
    ...theme.typography.caption,
    color: theme.palette.warning.main,
    marginBottom: theme.spacing(1),
    alignItems: 'flex-end',
  },
  button: {
    ...theme.typography.caption,
    color: theme.palette.warning.main,
    cursor: 'pointer',
    outline: 'inherit',
    background: 'none',
    border: 'none',
    paddingLeft: 0,
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    textDecoration: 'underline',
    fontSize: '0.9rem',
  },
  variants: {
    border: '1px solid #dcdcdcd6',
    padding: '0.75rem',
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
  },
  pricing: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: '0.875rem',
    color: 'black',
    alignItems: 'center',
    '& > div ': {
      display: 'flex',
      flexDirection: 'row',
      gap: '0.9rem',
      alignItems: 'center',
    },
    '& > div > span:first-child': {
      fontSize: '14px',
    },
    '& > div > span:last-child': {
      fontSize: '12px',
    },
  },
  productName: {
    color: 'black',
    '& > p': {
      fontSize: '0.875rem',
    },
  },
  inStock: {
    marginLeft: '1rem',
    color: '#4caf50',
  },
  notInStock: {
    marginLeft: '1rem',
    color: '#f44336',
  },
  product: {
    '&:not(:last-child)': {
      paddingBottom: '1rem',
      borderBottom: '1px solid #0000001f',
    },
    '&:only-child': {
      paddingBottom: '0rem',
      borderBottom: 'none',
    },
  },
  wrapper: {
    color: 'black',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    '& > div > div > span:first-child': {
      paddingRight: '0.5rem',
      color: '#0000008a',
      fontSize: '0.875rem',
    },
    '& > div > div:nth-child(3) > span:first-child': {
      paddingRight: '0.2rem',
      fontSize: '12px',
    },
    '& > div > div > span:nth-child(2)': {
      color: 'black',
    },
  },
  addToCart: {
    marginLeft: 'auto',
    gridArea: 'addToCart',

    // Fix grid in IE 10+
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
      '-ms-grid-row': 1,
      '-ms-grid-column': 5,
      '-ms-grid-row-span': 2,
    },
  },
  sku: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

const CataloguePageListContentItemWarning: React.FC<CataloguePageListContentItemWarningProps> = ({
  classes: pClasses,
  className,
  product,
  type,
}) => {
  const priceGroup = useRecoilValue(customerPriceGroupState);
  const classes = useStyles({ classes: pClasses, className });
  const warningText = product.taxonomies?.includes(`/variants/${priceGroup}`);
  const [showVariants, setShowVariants] = useState(false);
  const handleShowVariants = React.useCallback(() => {
    setShowVariants((prev) => !prev);
  }, []);

  return warningText ? (
    <React.Suspense fallback={<></>}>
      <div className={clsx(classes.root, className)}>
        <CataloguePageListContentItemWarningButton
          handleShowVariants={handleShowVariants}
          showVariants={showVariants}
          type={type}
        />
        {showVariants ? <CatalogueListContentItemWarningVariants product={product} /> : null}
      </div>
    </React.Suspense>
  ) : null;
};

const CataloguePageListContentItemWarningButton: React.FC<{
  handleShowVariants: () => void;
  showVariants: boolean;
  type: 'detail' | 'grid';
}> = ({ handleShowVariants, showVariants, type }) => {
  const classes = useStyles();
  const t = useTranslateWithFallback();

  return type === 'detail' ? (
    <button className={classes.button} onClick={handleShowVariants}>
      <InfoIcon fontSize='small' />
      {showVariants
        ? t('cataloguePage/warningButton/hideCheaperProducts')
        : t('cataloguePage/warningButton/showCheaperProducts')}
    </button>
  ) : (
    <>{t('cataloguePage/warningButton/otherOptions')}</>
  );
};

const CatalogueListContentItemWarningVariants: React.FC<{ product: CatalogueProduct }> = ({ product }) => {
  const classes = useStyles();
  const variants = useRecoilValue(productVariantsQuery(product.pn as string));
  const currency = useRecoilValue(currencyState);
  const t = useTranslateWithFallback();

  return (
    <div className={classes.variants}>
      {variants.rows.slice(1).map((variant, index) => (
        <div key={index} className={classes.product}>
          <div className={classes.wrapper}>
            <div className={classes.productName}>
              <p>
                <strong>{variant.displayName && variant.displayName[0].text}</strong>
              </p>
              <span className={classes.sku}>
                <span>
                  {t('common/code')}: {variant.sku || ''}
                </span>
              </span>
            </div>
            <div style={{ paddingTop: '0.875rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              {variant.stockQuantity && variant.stockQuantity.available ? (
                <span className={classes.inStock}>
                  {variant.stockQuantity.detail && variant.stockQuantity.detail[0].quantity}{' '}
                  {t('productPage/pcsInStock')}
                </span>
              ) : (
                <span className={classes.notInStock}>{t('common/notInStock')}</span>
              )}
              <div>
                <span>{t('common/withTax')}</span>
                <span style={{ fontSize: '22.5px', fontWeight: 500 }}>
                  {currencyFormatter(variant.priceWithTax, currency)}
                </span>
              </div>

              <div>
                <span>{t('common/withoutTax')} </span>
                <span style={{ fontSize: '16px' }}>{currencyFormatter(variant.price, currency)}</span>
              </div>
            </div>
          </div>
          <div className={classes.pricing}>
            <div>
              {variant && variant.extra && variant.extra.packSize && variant.extra.type === 'pack' ? (
                <>
                  <span>
                    {t('common/withTaxPerPc')}{' '}
                    <span style={{ fontSize: '16px' }}>
                      {currencyFormatter(
                        variant.priceWithTax ? variant.priceWithTax / variant.extra.packSize : variant.priceWithTax,
                        currency,
                      )}
                    </span>
                  </span>
                  <span>
                    {t('common/withTaxPerPc')}{' '}
                    <span style={{ fontSize: '16px' }}>
                      {currencyFormatter(
                        variant.price ? variant.price / variant.extra.packSize : variant.price,
                        currency,
                      )}
                    </span>
                  </span>
                </>
              ) : null}
            </div>
            <CataloguePageListContentItemRowAddToCart className={classes.addToCart} product={variant} />
          </div>
        </div>
      ))}
    </div>
  );
};

export default CataloguePageListContentItemWarning;
